import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Axios from "axios";
import {
  NavDrawer,
  NavDrawerBody,
  NavDrawerHeader,
  NavItem,
  NavSectionHeader,
  NavSubItem,
  Hamburger,
} from "@fluentui/react-nav-preview";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import ConfigurationContent from "./ConfigurationContent";
import DevicesContent from "./DevicesContent";
import LogicFunctionContent from "./LogicFunctionContent";
import ZonesContent from "./ZonesContent";
import OutputZonesContent from "./OutputZonesContent";
import MainContent from "./MainContent";
import "../Styles/PanelType.css";
import ConfigFile from "./ParsePanelData";
import defaultPanelData from "../Assets/defaultPanelData/defaultPanelData.json";
import { Menu, MenuItem } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import UserProfileHeader from "./UserProfileHeader";

class Panel {
  constructor(configFile) {
    // Ensure VT5000_CONFIG is always initialized
    this.VT5000_CONFIG = configFile?.VT5000_CONFIG || {
      GeneralParameters: {},
      ArgusTablePr: [],
      Codes: {},
      DaylightSaving: {},
      FlogicPr: [],
      Holidays: [],
      OutputZones: [],
      TimeIntervals: [],
      VTNet: [],
      WeeklyTrends: [],
      ZoneTable: [],
    };
  }
}

const subNavItems = ["Configuration", "Devices", "Logic Function", "Zones", "Output Zones"];
// const subNavItems = ["Configuration", "Devices", "Zones", "Output Zones"];

const parsePanelsToConfig = (panelData) => {
  const configFile = new ConfigFile();

  // Safely parse and set defaults
  configFile.VT5000_CONFIG = {
    ArgusTablePr: panelData.argustablepr || [],
    Codes: panelData.codes || {},
    DaylightSaving: panelData.daylightsaving || {},
    FlogicPr: panelData.flogicpr || [],
    GeneralParameters: panelData.generalparameters || {},
    Holidays: panelData.holidays || [],
    OutputZones: panelData.outputzones || [],
    TimeIntervals: panelData.timeintervals || [],
    VTNet: panelData.vtnets || [],
    WeeklyTrends: panelData.weeklytrends || [],
    ZoneTable: panelData.zonetable || [],
    Dependency: panelData.dependency || [],
  };

  return configFile;
};

const PanelType = ({ isAdvancedMode }) => {
  const [panelData, setPanelData] = useState([]);
  const [selectedPanelIndex, setSelectedPanelIndex] = useState(null);
  const [selectedSubNavItem, setSelectedSubNavItem] = useState("Main");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newPanelName, setNewPanelName] = useState("");
  const [selectedCheckbox, setSelectedCheckbox] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPanels();
  }, []);

  const fetchPanels = () => {
    const email = localStorage.getItem('userEmail');
    Axios.post(`/get_panels`, { email })
      .then((response) => {
        if (response.data.success) {
          const panels = response.data.panels.map((panelData) => new Panel(parsePanelsToConfig(panelData)));
          console.log(panels);
          setPanelData(panels);
        } else {
          console.error("Failed to fetch panels.");
        }
      })
      .catch((error) => console.error("An error occurred while fetching panels:", error));
  };

  const handleCreatePanel = () => {
    const email = localStorage.getItem("userEmail");
    
    // Check if panel with the same name already exists
    if (panelData.some(panel => panel.panel_name === newPanelName)) {
      toast.warning("A panel with this name already exists!");
      return;
    }

    let newPanelData = { ...defaultPanelData };
    // , generalParameters: { panelName: newPanelName, loopCardNb: selectedCheckbox } 
    newPanelData.GeneralParameters.panelName = newPanelName;
    newPanelData.GeneralParameters.loopCardNb = selectedCheckbox;

    Axios.post(`/check_panel_exist`, {
      email,
      panelName: newPanelName,
      panel: newPanelData,
    })
      .then((response) => {
        if (response.data.success) {
          toast.success("Panel created successfully!");
          fetchPanels();
          setIsDialogOpen(false);
          setNewPanelName("");
        } else {
          toast.error("Failed to create panel.");
        }
      })
      .catch((error) => {
        console.error("Error creating panel:", error);
        toast.error("Error creating panel!");
      });
  };

  const updatePanels = () => {
    fetchPanels();
  };

  const handleNavItemClick = (index) => {
    setSelectedPanelIndex(index);
    setSelectedSubNavItem("Main"); // Default to Main when a panel is selected
  };

  const handleSubNavItemClick = (subNavItem) => {
    setSelectedSubNavItem(subNavItem);
  };

  const handleCheckboxChange = (index) => {
    setSelectedCheckbox(index);
  };

  const resetPanelSelection = () => {
    setSelectedPanelIndex(null);
  };

  const handleDeletePanel = () => {
    const selectedPanel = panelData[selectedPanelIndex];
    if (!selectedPanel) return;
  
    const panelName = selectedPanel.VT5000_CONFIG.GeneralParameters.panelName;
    const email = localStorage.getItem("userEmail");
  
    Axios.post(`/delete_panel`, {
      email,
      panelName,
    })
      .then((response) => {
        if (response.data.success) {
          toast.success(`Panel '${panelName.replace(/\u0000/g, '')}' deleted.`);
          fetchPanels(); // Refresh sidebar
          resetPanelSelection(); // Deselect deleted panel
        } else {
          toast.error("Failed to delete panel.");
        }
      })
      .catch((error) => {
        console.error("Delete panel failed!", error);
        toast.error("Error deleting panel.");
      });
  
    setDeleteDialogOpen(false);
  };

  // Render specific content based on the selected subnav item
  const renderContent = () => {
    const selectedPanel = panelData[selectedPanelIndex];

    if (!selectedPanel) {
      return <div>Select a panel to see its content.</div>;
    }

    switch (selectedSubNavItem) {
      case "Configuration":
        return <ConfigurationContent project={selectedPanel} setSelectedSubNavItem={setSelectedSubNavItem} updatePanels={updatePanels} isAdvancedMode={isAdvancedMode} />;
      case "Devices":
        return <DevicesContent project={selectedPanel} />;
      case "Logic Function":
        return <LogicFunctionContent panel={selectedPanel} />;
      case "Zones":
        return <ZonesContent panel={selectedPanel} />;
      case "Output Zones":
        return <OutputZonesContent panel={selectedPanel} />;
      default:
        return <MainContent project={selectedPanel} updatePanels={updatePanels} isAdvancedMode={isAdvancedMode} />;
    }
  };

  return (
    <div className="dashboard">
      <UserProfileHeader isAdmin={true} />
      <div style={{ display: "flex", height: "100vh", flexDirection: "row" }}>
        <NavDrawer open={true} type="inline" style={{ width: "250px", borderRight: "1px solid #ddd", boxShadow: "2px 0 5px rgba(0,0,0,0.05)", backgroundColor: "#fff" }}>
          <NavDrawerHeader>
            <Hamburger onClick={() => navigate("/dashboard")} />
          </NavDrawerHeader>
          <NavDrawerBody>
            <NavSectionHeader>Panels</NavSectionHeader>
            <Button onClick={() => setIsDialogOpen(true)}>New Panel</Button>
            {panelData.map((panel, index) => (
              <React.Fragment key={index}>
                <NavItem
                  onClick={() => handleNavItemClick(index)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({
                      mouseX: e.clientX - 2,
                      mouseY: e.clientY - 4,
                      panelIndex: index,
                    });
                  }}
                  style={{
                    color: selectedPanelIndex === index ? "#c00d0d" : undefined,
                    marginLeft: "10px"
                  }}
                >
                  {panel.VT5000_CONFIG.GeneralParameters?.panelName.replace(/\u0000/g, '') || "Unnamed Panel"}
                </NavItem>
                {selectedPanelIndex === index && (
                  <div style={{ marginLeft: "20px" }}>
                    {subNavItems.map((subItem, subIndex) => (
                      <NavSubItem
                        key={subIndex}
                        onClick={() => handleSubNavItemClick(subItem)}
                        style={{
                          color: selectedSubNavItem === subItem ? "#c00d0d" : undefined,
                        }}
                      >
                        {subItem}
                      </NavSubItem>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}
          </NavDrawerBody>
        </NavDrawer>

        <div style={{ flex: 1, padding: "16px", color: "black", overflow: "auto" }}>
          {renderContent()}
        </div>

        <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
          <DialogTitle>CREATE NEW PANEL</DialogTitle>
          <DialogContent>
            <TextField
              label="Panel Name"
              fullWidth
              value={newPanelName}
              style={{ marginTop: "10px" }}
              onChange={(e) => setNewPanelName(e.target.value)}
            />
            <div style={{ fontSize: "20px", marginBottom: "10px", marginTop: "9%" }}>Loop Expansion Cards</div>
            <div style={{ display: "flex", justifyContent: "center", gap: "1px" }}>
              {[0, 1, 2, 3, 4].map((num) => (
                <div key={num} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "60px", height: "60px", border: "2px solid black", borderRadius: "5px", cursor: "pointer", background: selectedCheckbox === num ? "#C00D0D" : "#FFF" }}
                  onClick={() => handleCheckboxChange(num)}>
                  <span style={{ fontSize: "30px", fontWeight: "bold", color: selectedCheckbox === num ? "#FFF" : "#000" }}>{num}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: "20px", marginTop: "10px" }}>Each card is equal to 2 Loop</div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePanel} color="primary">Create</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Project Deletion</DialogTitle>
          <DialogContent>Are you sure you want to delete this project?</DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">Cancel</Button>
            <Button onClick={handleDeletePanel} color="primary">Delete</Button>
          </DialogActions>
        </Dialog>
        <Menu
          open={contextMenu !== null}
          onClose={() => setContextMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                minWidth: 100,
                backgroundColor: "#fff",
                paddingY: 0.5,
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setSelectedPanelIndex(contextMenu.panelIndex);
              setContextMenu(null);
              setDeleteDialogOpen(true);
            }}
            sx={{
              color: "error.main",
              fontWeight: 500,
              gap: 1,
              paddingY: 1,
              '&:hover': {
                backgroundColor: "#ffe6e6",
              },
            }}
          >
            <DeleteIcon fontSize="small" />
            Delete
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default PanelType;
